import React from "react";
import { useVideoStore } from "./store/use-videos-store";
import { Grid } from "@mui/material";
import { Button, message, Popconfirm, Tag, Progress } from "antd";
import {
  ClearOutlined,
  PlaySquareOutlined,
  SaveOutlined,
  VideoCameraAddOutlined,
  WifiOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import { VideoPlayerModal, VideoRecorderNative } from "../../../../libs/shared";
import { workflowContainerVideosPost } from "../../../../libs/apis";
import Swal from "sweetalert2";
import { useOpen } from "../../../../libs/hooks";
import { removeImageBlob, getImageBlob, analyzeConnectionQuality } from "../../../../libs/helpers";

const VideoCajasRecorder = () => {
  const container = useVideoStore((state) => state.container);
  const [loading, setLoading] = React.useState(false);
  const [uploadProgress, setUploadProgress] = React.useState(0);
  const [emptyInternalVideo, setEmptyInternalVideo] = React.useState("");
  const [showModal, setShowModal] = React.useState(false);
  const [continuoModal, setContinuoModal] = React.useState(false);
  const [playerOpen, setPlayerOpen] = React.useState(false);
  
  // ✅ NUEVO: Estado de conexión (igual que VideoVacio)
  const [connectionInfo, setConnectionInfo] = React.useState(() => analyzeConnectionQuality());
  
  const {
    open: openViConVa,
    handleClose: handleCloseViConVa,
    handleOpen: handleOpenViConVa,
  } = useOpen();

  // ✅ NUEVO: Monitorear conexión (igual que VideoVacio)
  React.useEffect(() => {
    const updateConnection = () => {
      const newInfo = analyzeConnectionQuality();
      setConnectionInfo(newInfo);
    };

    window.addEventListener("online", updateConnection);
    window.addEventListener("offline", updateConnection);

    const connection = (navigator as any).connection;
    if (connection) {
      connection.addEventListener("change", updateConnection);
    }

    const interval = setInterval(updateConnection, 30000);

    return () => {
      window.removeEventListener("online", updateConnection);
      window.removeEventListener("offline", updateConnection);
      if (connection) {
        connection.removeEventListener("change", updateConnection);
      }
      clearInterval(interval);
    };
  }, []);

  React.useEffect(() => {
    if (!continuoModal) {
      setContinuoModal(false);
      handleOpenViConVa();
    }
  }, [continuoModal, handleOpenViConVa]);

  const handleShowModal = () => {
    setShowModal(true);
    setContinuoModal(true);
  };

  const handleCloseModal = () => {
    handleCloseViConVa();
    setShowModal(false);
    setContinuoModal(false);
  };

  const handleVideoSaved = (key: string) => {
    setEmptyInternalVideo(key);
  };

  const clearVideo = async () => {
    await removeImageBlob(emptyInternalVideo);
    setEmptyInternalVideo("");
    setPlayerOpen(false);
    setUploadProgress(0);
  };

  const openPlayer = () => {
    setPlayerOpen(true);
  };

  const closePlayer = () => {
    setPlayerOpen(false);
  };

  // ✅ FUNCIÓN DE UPLOAD CON VALIDACIÓN (igual que VideoVacio)
  const handleUpload = React.useCallback(async () => {
    if (!emptyInternalVideo) {
      Swal.fire({
        title: "No hay video",
        text: "Graba un video antes de continuar.",
        icon: "warning",
        confirmButtonColor: "#1677ff",
      });
      return;
    }

    if (!container || container.length < 3) {
      Swal.fire({
        title: "Contenedor inválido",
        text: "Ingresa un número de contenedor válido (mínimo 3 caracteres).",
        icon: "warning",
        confirmButtonColor: "#1677ff",
      });
      return;
    }

    // ✅ VERIFICAR CONEXIÓN (igual que VideoVacio)
    const currentConnection = analyzeConnectionQuality();
    
    if (!currentConnection.canUpload) {
      let message = "";
      
      switch (currentConnection.quality) {
        case "sin-internet":
          message = "No hay conexión a internet. Conecta y vuelve a intentar.";
          break;
        case "internet-lento":
          message = "Tu conexión es muy lenta para subir videos. Conecta a WiFi.";
          break;
        case "internet-regular":
          message = "Tu conexión es inestable. Se recomienda WiFi para videos.";
          break;
        default:
          message = "Conexión insuficiente para subir el video.";
      }
      
      Swal.fire({
        title: "Conexión insuficiente",
        text: message,
        icon: "warning",
        confirmButtonColor: "#1677ff",
        confirmButtonText: "Entendido",
      });
      return;
    }

    const getVideo = await getImageBlob(emptyInternalVideo);

    const formData = new FormData();
    formData.append("video", getVideo!, "video.mp4");
    formData.append("type", "videoCajas");
    formData.append("containerType", "full");
    formData.append("container", container);

    try {
      setLoading(true);
      setUploadProgress(0);

      console.log(`🎥 Subiendo video de cajas grabado - ${currentConnection.message}`);

      // Simular progreso
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          const increment = currentConnection.quality === "internet-bueno" ? 15 : 10;
          const newValue = prev + increment;
          return newValue > 90 ? 90 : newValue;
        });
      }, 500);

      const { data: dataPost } = await workflowContainerVideosPost(formData);
      
      clearInterval(progressInterval);
      setUploadProgress(100);

      Swal.fire({
        title: "Video Cajas subido exitosamente",
        icon: "success",
        confirmButtonColor: "#1677ff",
        timer: 3000,
        timerProgressBar: true,
      });
      clearVideo();
    } catch (error: any) {
      setUploadProgress(0);
      
      let errorTitle = "Error al subir video";
      let errorMessage = error.toString();
      
      if (error.message?.includes("Sesión expirada")) {
        errorTitle = "Sesión expirada";
        errorMessage = "Tu sesión ha caducado. Inicia sesión nuevamente.";
      } else if (error.message?.includes("conexión")) {
        errorTitle = "Problema de conexión";
        errorMessage = "Verifica tu conexión a internet e intenta nuevamente.";
      }

      Swal.fire({
        title: errorTitle,
        text: errorMessage,
        icon: "error",
        confirmButtonColor: "#1677ff",
        confirmButtonText: "Entendido",
      });
    } finally {
      setLoading(false);
    }
  }, [emptyInternalVideo, container]);

  // ✅ OBTENER ESTADO VISUAL (igual que VideoVacio)
  const getConnectionDisplay = () => {
    const { quality, message, description, speedMbps } = connectionInfo;
    
    switch (quality) {
      case "sin-internet":
        return {
          icon: <ExclamationCircleOutlined />,
          color: "red",
          text: message,
          description: description,
        };
      case "internet-lento":
        return {
          icon: <ExclamationCircleOutlined />,
          color: "volcano",
          text: message,
          description: `${description} (${speedMbps.toFixed(1)} Mbps)`,
        };
      case "internet-regular":
        return {
          icon: <ClockCircleOutlined />,
          color: "orange",
          text: message,
          description: `${description} (${speedMbps.toFixed(1)} Mbps)`,
        };
      case "internet-bueno":
        return {
          icon: <WifiOutlined />,
          color: "green",
          text: message,
          description: `${description} (${speedMbps.toFixed(1)} Mbps)`,
        };
      default:
        return {
          icon: <CheckCircleOutlined />,
          color: "blue",
          text: "Estado desconocido",
          description: "Verificando conexión...",
        };
    }
  };

  const connectionDisplay = getConnectionDisplay();

  return (
    <Grid
      item
      xs={12}
      sm={10}
      md={8}
      lg={6}
      xl={4}
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        gap: "5px",
      }}
    >
      {/* ✅ INDICADOR DE CONEXIÓN (igual que VideoVacio) */}
      <div style={{ marginBottom: "10px", textAlign: "center" }}>
        <Tag
          color={connectionDisplay.color}
          icon={connectionDisplay.icon}
          style={{ fontSize: "12px", padding: "4px 8px" }}
        >
          {connectionDisplay.text}
        </Tag>
        <div style={{ fontSize: "11px", color: "#666", marginTop: "2px" }}>
          {connectionDisplay.description}
        </div>

        {!connectionInfo.canUpload && (
          <div style={{ fontSize: "10px", color: "#ff4d4f", marginTop: "4px" }}>
            ⚠️ {connectionInfo.quality === "sin-internet" ? 
              "Conecta a internet para subir videos" : 
              "Se necesita mejor conexión (WiFi recomendado)"
            }
          </div>
        )}
      </div>
      {emptyInternalVideo ? (
        <Button
          color="green"
          variant="solid"
          onClick={openPlayer}
          size="large"
          style={{
            width: "100%",
            minWidth: "320px",
            maxWidth: "320px",
            fontSize: "1em",
            fontWeight: "bold",
            marginTop: "10px",
          }}
          icon={<PlaySquareOutlined />}
        >
          REPRODUCIR VIDEO CAJAS
        </Button>
      ) : (
        <Button
          type={!emptyInternalVideo ? "dashed" : "primary"}
          size="large"
          danger={!emptyInternalVideo ? true : false}
          style={{
            width: "100%",
            minWidth: "320px",
            maxWidth: "320px",
            fontSize: "1.1em",
            fontWeight: "bold",
            marginTop: "10px",
          }}
          icon={<VideoCameraAddOutlined />}
          onClick={handleShowModal}
        >
          GRABAR VIDEO CAJAS
        </Button>
      )}
      
      {showModal ? (
        <VideoRecorderNative
          open={openViConVa}
          handleClose={handleCloseModal}
          onVideoSaved={handleVideoSaved}
        />
      ) : null}

      <VideoPlayerModal
        videoKey={emptyInternalVideo}
        open={playerOpen}
        onClose={closePlayer}
        onClear={clearVideo}
      />
      
      {emptyInternalVideo && (
        <div
          style={{
            background: "lightcyan",
            borderRadius: "10px",
            padding: "20px",
            width: "100%",
            maxWidth: "400px",
            marginTop: "10px",
          }}
        >
          <p><strong>Video Cajas Grabado:</strong> Listo para subir</p>

          {/* Barra de progreso */}
          {loading && (
            <div>
              <Progress
                percent={uploadProgress}
                status={uploadProgress < 100 ? "active" : "success"}
                style={{ marginBottom: "10px" }}
              />
              <div
                style={{ fontSize: "12px", color: "#666", textAlign: "center" }}
              >
                {uploadProgress < 100 ? 
                  `Subiendo video de cajas... ${uploadProgress}%` : 
                  "Finalizando..."
                }
              </div>
            </div>
          )}

          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "10px",
              marginTop: "10px",
            }}
          >
            <Popconfirm
              title="¿Subir video de cajas al servidor?"
              description="El video se enviará directamente sin almacenamiento offline"
              onConfirm={handleUpload}
              zIndex={1601}
              okText="Sí, subir"
              cancelText="Cancelar"
              disabled={!connectionInfo.canUpload}
            >
              <Button
                type="primary"
                size="large"
                icon={<SaveOutlined />}
                loading={loading}
                disabled={
                  !container || 
                  loading || 
                  container.length < 3 || 
                  !connectionInfo.canUpload
                }
                style={{ fontSize: "1.1em" }}
              >
                SUBIR VIDEO
              </Button>
            </Popconfirm>
            <Button
              type="dashed"
              size="large"
              icon={<ClearOutlined />}
              onClick={clearVideo}
              style={{ fontSize: "1.1em" }}
              disabled={loading}
            >
              LIMPIAR
            </Button>
          </div>

          {/* Indicador de modo de subida */}
          <div style={{ marginTop: "10px", textAlign: "center" }}>
            {!connectionInfo.canUpload ? (
              <Tag color="red" icon={<ExclamationCircleOutlined />}>
                ❌ No se puede subir - {connectionInfo.message}
              </Tag>
            ) : (
              <Tag color="green" icon={<CheckCircleOutlined />}>
                ✅ Listo para subir - {connectionInfo.isWiFi ? "WiFi" : "Datos móviles"}
              </Tag>
            )}
          </div>
        </div>
      )}
    </Grid>
  );
};

export default VideoCajasRecorder;